import { FC, Suspense, useEffect, useMemo, useState } from 'react';

import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next/pages';

import { Button, Modal } from 'antd';

import { Container } from '@//components/common';
import { createPaymentRequest, getTariffs } from '@/api/tariff';
import TariffsTable from '@/components/TariffsTable';
import { MAKE_REGISTRATION_URL } from '@/constants/routes';
import { useUserState } from '@/context/user.context';
import useMobile from '@/hooks/use-mobile';
import { TranslationNamespace } from '@/i18n';
import { CompanyType } from '@/types/common-entities';
import { Tariff } from '@/types/tariff';
import { useQuery } from '@tanstack/react-query';

import { FishIcons, FishIconsMobile } from './images/fishes';

import styles from './Tariffs.module.scss';

const Tariffs: FC = () => {
	const router = useRouter();
	const { user, balance } = useUserState();
	const { isMobile } = useMobile();
	const [isAcceptedModalOpen, setIsAcceptedModalOpen] = useState(false);
	const [tariffChoiceDisable, setTariffChoiceDisable] = useState(false);
	const [userIsStorage, setUserIsStorage] = useState(false);

	const t = useTranslation(TranslationNamespace.Marketing).t;

	const {
		data: tariffsData,
		isLoading = true,
		isError,
	} = useQuery({
		queryKey: ['tariffsInfo', router.locale],
		queryFn: async () => await getTariffs({ lang: router.locale }),
	});

	useEffect(() => {
		if (router.asPath.includes('#tariffs')) {
			const el = document.querySelector('#tariffs');
			el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}, []);

	/** Пользователь авторизован? */
	const authed = useMemo(() => Boolean(user), [user]);

	const onTariffChoiceHandler = async (params: {
		tariffInfo: Tariff.TariffWithSettings;
		perYear?: boolean;
		per_three_month?: boolean;
	}) => {
		if (!authed) {
			return router.push(MAKE_REGISTRATION_URL({ redirectTo: router.asPath }));
		}

		try {
			setTariffChoiceDisable(true);
			if (params.perYear) {
				await createPaymentRequest({
					company: balance?.company?.id,
					type: 1,
					tariff: params.tariffInfo.id,
					per_year: params.perYear,
				});
			} else {
				await createPaymentRequest({
					company: balance?.company?.id,
					type: 1,
					tariff: params.tariffInfo.id,
					per_three_month: params.per_three_month,
				});
			}
			setIsAcceptedModalOpen(true);
		} catch (error) {
			console.log(error);
		} finally {
			setTariffChoiceDisable(false);
		}
	};

	useEffect(() => {
		if (user) {
			setUserIsStorage(user.companyType === CompanyType.Warehouse);
		}
	}, [user]);

	if (isLoading) {
		return <Suspense />;
	}

	if (isError) {
		return null;
	}

	return (
		<section className={styles.section}>
			<Container>
				{isMobile ? (
					<FishIconsMobile className={styles.fishes} />
				) : (
					<FishIcons className={styles.fishes} />
				)}
				{!isMobile && <h2 className={styles.mobileTitle}>{t('main-landing.tariffs.title')}</h2>}
				<div className={styles.card} id="tariffs">
					<TariffsTable
						tariffsData={tariffsData}
						bestChoice={2}
						maxHeight="40rem"
						userIsAuth={authed}
						onTariffChoice={onTariffChoiceHandler}
						tariffChoiceDisable={tariffChoiceDisable}
						currentTariff={balance?.tariff?.id}
						currentPerYear={balance?.perYear}
						showButtons={!userIsStorage}
						isActiveTariff={balance?.activeTariff}
					/>
				</div>
			</Container>

			<Modal
				open={isAcceptedModalOpen}
				onCancel={() => setIsAcceptedModalOpen(false)}
				width="35%"
				footer={false}
				centered
				title={t('main-landing.tariffs.tariff-change-application-accepted')}
			>
				<p className="opacity-[.64] m-0 mb-[3rem]">
					{t(
						'main-landing.tariffs.our-manager-will-contact-you-shortly-a-bill-of-payment-will-be-available',
					)}
				</p>
				<Button type="primary" block onClick={() => setIsAcceptedModalOpen(false)}>
					{t('main-landing.tariffs.ok')}
				</Button>
			</Modal>
		</section>
	);
};
export default Tariffs;
